import React, { FC, ReactNode, useEffect, useState } from 'react';
import { Button, Table, Tooltip } from 'antd';
import { InfiniteScroll, SearchInput, Spin } from 'common/components';
import { getEmail, showSuccessMessage } from 'common/helpers';
import { useUserContext } from 'common/context';
import { DEFAULT_LIST_OFFSET, DEFAULT_PAGINATION_PAGE, LIST_LIMIT_0, LIST_LIMIT_20 } from 'common/config';
import { Plus } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { ISubdivision } from 'entities/Subdivision/Subdivision.models';
import { renderUserListSettingsRecords } from 'entities/Settings/Settings.helper';
import { UserAddModal, UserCard, UserEditCard } from 'entities/User/components';
import { IUser, IUserListParams } from 'entities/User/User.models';
import {
  getSubdivisionUsers,
  getSubdivisionUsersChunk,
  selectIsSubdivisionLoading,
  selectSubdivisionList,
  selectSubdivisionUserCount,
  selectSubdivisionUsers,
  setSubdivisionUsers,
} from 'entities/Subdivision/Subdivision.slice';
import { addUserSubdivision, getUserById, getUserList, selectIsUserLoading, selectUserList } from 'entities/User/User.slice';

interface IComponentProps {
  header: ReactNode;
  subdivision: ISubdivision | null;
}

export const SettingsSubdivisionUserListTab: FC<IComponentProps> = ({ header, subdivision }) => {
  const dispatch = useAppDispatch();
  // subdivision
  const subdivisionList = useAppSelector(selectSubdivisionList);
  const isSubdivisionLoading = useAppSelector(selectIsSubdivisionLoading);
  const subdivisionUsers = useAppSelector(selectSubdivisionUsers);
  const subdivisionUserCount = useAppSelector(selectSubdivisionUserCount);
  // user
  const userList = useAppSelector(selectUserList);
  const isUserLoading = useAppSelector(selectIsUserLoading);

  const [fetchLoading, setFetchLoading] = useState<boolean>(true);
  const [scrollLoading, setScrollLoading] = useState<boolean>(false);
  const [page, setPage] = useState<number>(DEFAULT_PAGINATION_PAGE);
  const [openUserAddModal, setOpenUserAddModal] = useState<boolean>(false);
  const [params, setParams] = useState<IUserListParams>({
    limit: LIST_LIMIT_20,
    offset: DEFAULT_LIST_OFFSET,
  });
  const { setOpenUserCard } = useUserContext();

  const onSearch = async (value?: string) => {
    const newParams = {
      ...params,
      search: value,
    };

    setParams(newParams);
    await dispatch(getSubdivisionUsers(newParams));
  };

  const onOpenUserAddModalClick = async () => {
    await dispatch(
      getUserList({ excludeSubdivisionId: subdivision?.id, limit: LIST_LIMIT_0, onSuccess: () => setOpenUserAddModal(true) }),
    );
  };

  const onAddSubdivisionUserClick = (value: IUser, onSuccess: () => void) => {
    if (subdivision) {
      dispatch(
        addUserSubdivision({
          userId: value.id,
          subdivisionId: subdivision.id,
          onSuccess: () => {
            showSuccessMessage(`Пользователь «${getEmail(value.email)}» добавлен в подразделение.`);
            onSuccess();
          },
        }),
      );
    }
  };

  useEffect(() => {
    const fetch = async () => {
      setFetchLoading(true);

      if (subdivision?.id) {
        const defaultParams = {
          ...params,
          subdivisionId: subdivision.id,
          search: undefined,
        };

        dispatch(setSubdivisionUsers([]));
        setParams(defaultParams);
        await dispatch(getSubdivisionUsers(defaultParams));
      }

      setFetchLoading(false);
    };

    fetch();
  }, [subdivision?.id]);

  return (
    <>
      <InfiniteScroll
        loading={scrollLoading}
        hasMore={!isSubdivisionLoading && subdivisionUsers.length < subdivisionUserCount}
        loader={<Spin />}
        scrollToTopTrigger={[params]}
        loadMore={async () => {
          setScrollLoading(true);
          setPage(page + 1);
          await dispatch(
            getSubdivisionUsersChunk({
              ...params,
              offset: LIST_LIMIT_20 * page,
            }),
          );
          setScrollLoading(false);
        }}
      >
        {header}

        <div className="mb-6 flex align-center">
          <div className="mr-4 flex-1">
            <SearchInput auto placeholder="Введите имя, фамилию или e-mail" clearDeps={[subdivision?.id]} onSearch={onSearch} />
          </div>

          <Tooltip title="Добавить сотрудника" placement="bottom">
            <Button
              className="button-circle primary"
              icon={<Plus className="icon-plus-white" />}
              onClick={onOpenUserAddModalClick}
            />
          </Tooltip>
        </div>

        <Table
          className={`mb-19 table-row-middle table-hover ${!subdivisionUsers.length && 'table-empty'}`}
          showHeader={false}
          pagination={false}
          size="middle"
          dataSource={subdivisionUsers.map((user) => ({ ...user, key: user.id }))}
          columns={renderUserListSettingsRecords()}
          loading={isSubdivisionLoading || isUserLoading}
          locale={{
            emptyText: fetchLoading ? (
              <Spin />
            ) : (
              <div className="flex direction-column align-center">
                <div className="mb-6 ff-regular fs-m color-dark-grey">В подразделении ещё нет пользователей.</div>

                <Button className="button-sm secondary" onClick={onOpenUserAddModalClick}>
                  Добавить пользователя
                </Button>
              </div>
            ),
          }}
          onRow={({ id }) => ({
            onClick: () => {
              dispatch(
                getUserById({
                  id,
                  onSuccess: () => setOpenUserCard(true),
                }),
              );
            },
          })}
        />
      </InfiniteScroll>

      <UserCard subdivisionId={subdivision?.id} />

      <UserEditCard />

      <UserAddModal
        open={openUserAddModal}
        placeholder="Найти и добавить сотрудника"
        loading={isUserLoading}
        userList={userList}
        checkSubdivision
        subdivisionList={subdivisionList}
        onClose={() => setOpenUserAddModal(false)}
        onAdd={onAddSubdivisionUserClick}
      />
    </>
  );
};
